import { useCpiInventorySummary } from '@gozoki/api'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { withRoutePermissions } from '~/utils/rbac'

export const Route = createFileRoute('/_authenticated/cpi-inventory')({
  staticData: {
    permission: ['read', 'network.network.network-dashboard'],
  },
  component: CpiInventoryPage,
})
withRoutePermissions(Route)

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—'

const TONE_COLOR = {
  ok: 'text-emerald-600',
  warn: 'text-amber-600',
  bad: 'text-red-600',
} as const

// Légende sous les compteurs de sorties : borne = fin de la dernière fenêtre de remplissage.
function SortiesCaption({
  activeUntil,
  detectionActive,
}: {
  activeUntil: string | null
  detectionActive: boolean
}) {
  if (activeUntil === null) {
    return (
      <p className="mt-2 text-xs text-gray-500">
        Aucun remplissage détecté — sorties non comptées (0).
      </p>
    )
  }
  if (detectionActive) {
    return (
      <p className={`mt-2 text-xs ${TONE_COLOR.warn}`}>
        🔄 Remplissage en cours — le comptage des sorties démarre à la fermeture de la fenêtre (
        {fmtDate(activeUntil)}). Ces chiffres vont évoluer.
      </p>
    )
  }
  return (
    <p className="mt-2 text-xs text-gray-500">
      Sorties depuis le dernier remplissage ({fmtDate(activeUntil)}).
    </p>
  )
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone?: 'ok' | 'warn' | 'bad'
}) {
  const color = tone ? TONE_COLOR[tone] : 'text-gray-900'
  return (
    <div className="flex flex-col rounded-lg border border-gray-200 bg-white px-4 py-3">
      <span className="text-xs uppercase tracking-wide text-gray-500">{label}</span>
      <span className={`text-2xl font-semibold ${color}`}>{value}</span>
    </div>
  )
}

function CpiInventoryPage() {
  const { data, isLoading, isError } = useCpiInventorySummary()
  const [selectedStoreId, setSelectedStoreId] = useState<number | 'all'>('all')

  if (isLoading) return <div className="p-6 text-gray-500">Chargement…</div>
  if (isError) return <div className="p-6 text-red-600">Erreur de chargement.</div>
  if (!data || data.length === 0)
    return (
      <div className="p-6 text-gray-500">
        Aucune boutique avec l'inventaire RFID CPI activé (<code>cpi_inventory_enabled</code>).
      </div>
    )

  const visibleData =
    selectedStoreId === 'all' ? data : data.filter((s) => s.store.id === selectedStoreId)

  return (
    <div className="space-y-8 p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Inventaire — manuel vs auto (RFID CPI)
          </h1>
          <p className="text-sm text-gray-500">
            Détection en <strong>mode observation</strong> : les statuts sont calculés et affichés,
            sans action commerciale. À calibrer (faux positifs) avant activation.
          </p>
        </div>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs uppercase tracking-wide text-gray-500">Boutique</span>
          <select
            value={selectedStoreId}
            onChange={(e) =>
              setSelectedStoreId(e.target.value === 'all' ? 'all' : Number(e.target.value))
            }
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
          >
            <option value="all">Toutes les boutiques</option>
            {data.map((s) => (
              <option key={s.store.id} value={s.store.id}>
                {s.store.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {visibleData.map((s) => {
        const blindRate =
          s.manual.inStock > 0 ? Math.round((s.auto.neverSeen / s.manual.inStock) * 100) : 0
        return (
          <section key={s.store.id} className="space-y-4 rounded-xl border border-gray-200 p-5">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-lg font-semibold text-gray-900">{s.store.label}</h2>
              <span className="text-xs text-gray-500">
                Dernier sync RFID&nbsp;: <strong>{fmtDate(s.lastSyncAt)}</strong> · seuils&nbsp;:{' '}
                {s.thresholds.presumedAfterMinutes}min / {s.thresholds.stolenAfterMinutes}min
              </span>
            </div>

            {s.cpiDown && (
              <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm font-medium text-red-700">
                ⚠️ CPI hors ligne (<code>cpi_down</code>) — la détection est en pause (pas de faux «
                volé »).
              </div>
            )}

            <div
              className={
                s.restock.detectionActive
                  ? 'rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm text-emerald-800'
                  : 'rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm text-gray-600'
              }
            >
              <div className="font-medium">
                {s.restock.detectionActive ? (
                  <>🟢 En remplissage — détection active jusqu'à {fmtDate(s.restock.activeUntil)}</>
                ) : (
                  <>
                    ⏸️ Détection en pause (hors remplissage) — dernier restock&nbsp;:{' '}
                    {fmtDate(s.restock.lastDetectedAt)}
                  </>
                )}
              </div>
              <div className="mt-0.5 text-xs opacity-80">
                EPC lus / {s.restock.config.windowMinutes}&nbsp;min&nbsp;:{' '}
                <strong>{s.restock.recentEpcCount}</strong> · seuil restock&nbsp;:{' '}
                {s.restock.config.epcThreshold} · fenêtre&nbsp;: {s.restock.config.detectionHours}h
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-600">
                  Manuel (inventaire classique)
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  <Stat label="En stock" value={s.manual.inStock} />
                  <Stat label="Sortis inventaire" value={s.manual.inventoryDeleted} />
                  <Stat label="Vendus" value={s.manual.sold} />
                </div>
                <SortiesCaption
                  activeUntil={s.restock.activeUntil}
                  detectionActive={s.restock.detectionActive}
                />
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-600">
                  Auto (présence RFID)
                </h3>
                <div className="grid grid-cols-4 gap-3">
                  <Stat label="Vus récemment" value={s.auto.seenRecently} tone="ok" />
                  <Stat label="Jamais lus" value={s.auto.neverSeen} tone="warn" />
                  <Stat label="Présumé volé" value={s.auto.presumedStolen} tone="warn" />
                  <Stat label="Volé" value={s.auto.stolen} tone="bad" />
                </div>
              </div>
            </div>

            <p className="text-xs text-gray-500">
              Angle mort RFID (jamais lus / en stock)&nbsp;:{' '}
              <strong className={blindRate > 50 ? 'text-amber-600' : 'text-gray-700'}>
                {blindRate}%
              </strong>{' '}
              — proxy du risque de faux positifs.
            </p>

            <div>
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-600">
                Articles disparus ({s.missing.length})
              </h3>
              {s.missing.length === 0 ? (
                <p className="text-sm text-gray-400">Aucun article présumé volé ou volé.</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-gray-500">
                      <tr>
                        <th className="px-3 py-2 font-medium">EPC</th>
                        <th className="px-3 py-2 font-medium">Produit</th>
                        <th className="px-3 py-2 font-medium">Libellé</th>
                        <th className="px-3 py-2 font-medium">État</th>
                        <th className="px-3 py-2 font-medium">Dernière vue</th>
                        <th className="px-3 py-2 font-medium">Perdu à</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.missing.map((a) => (
                        <tr key={a.id} className="border-t border-gray-100">
                          <td className="px-3 py-2 font-mono text-xs">{a.epc}</td>
                          <td className="px-3 py-2">{a.productReference}</td>
                          <td className="px-3 py-2">{a.productLabel ?? '—'}</td>
                          <td className="px-3 py-2">
                            <span
                              className={
                                a.state === 'STOLEN'
                                  ? 'font-medium text-red-600'
                                  : 'font-medium text-amber-600'
                              }
                            >
                              {a.state === 'STOLEN' ? 'Volé' : 'Présumé volé'}
                            </span>
                          </td>
                          <td className="px-3 py-2">{fmtDate(a.lastSeenAt)}</td>
                          <td className="px-3 py-2">{fmtDate(a.lostAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        )
      })}
    </div>
  )
}
